import React, { useState, useEffect, useCallback } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { trackException } from '../services/appInsightsService';
import ReadOnlyFieldset, { ReadOnlyNote } from './ui/ReadOnlyFieldset';

const BASE_URL = () =>
    (process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api');

/**
 * PlanEditor — Admin component for listing and editing subscription plans.
 *
 * Lists all plans (active + inactive) and allows partial updates:
 * - Name, DisplayTier, MonthlyPriceCents, Active
 * - Limits: MaxParticipants, MaxTargetLanguages, MaxSessionDurationMinutes
 * - Features: TtsEnabled, BroadcastEnabled
 *
 * Meeting AI has no plan flag: points fund every Ask AI / summary call on every plan, and the
 * server's charge-time reservation refuses an unfunded caller (402 buy_pack).
 *
 * The Live Broadcast toggle is the supported, surgical way to grant that capability to a plan
 * whose stored catalog document predates the field — the alternative is POST /api/plans/seed,
 * which rewrites all 19 plans from current code. Production plan_ios_pro_v1 was exactly such a
 * document, which is why every Pro broadcast start was refused with an upgrade paywall.
 */
const PlanEditor = ({ readOnly = false }) => {
    const [plans, setPlans] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    const fetchPlans = useCallback(async () => {
        setIsLoading(true);
        setStatus({ type: '', message: '' });
        try {
            const response = await fetchWithAuth(`${BASE_URL()}/platform/plans`, { method: 'GET' });
            if (response.ok) {
                const data = await response.json();
                setPlans(Array.isArray(data) ? data : data.plans || []);
            } else {
                setStatus({ type: 'error', message: `Failed to load plans: ${response.status}` });
            }
        } catch (err) {
            trackException(err, { operation: 'PlanEditor.fetchPlans' });
            setStatus({ type: 'error', message: err.message });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    /** The editable projection of a stored plan — the shape `editForm` holds, and the baseline
     *  every "did anything actually change?" comparison is made against. */
    const projectPlan = (plan) => ({
        name: plan.name || '',
        displayTier: plan.displayTier || '',
        monthlyPriceCents: plan.monthlyPriceCents || 0,
        active: plan.active !== false,
        limits: {
            // Nullish coalescing, not `||` — a real stored 0 (e.g. a broadcast listener cap
            // of "not yet set") must render as 0, not be replaced by an empty input.
            maxParticipantsPerSession: plan.limits?.maxParticipantsPerSession ?? '',
            maxTargetLanguages: plan.limits?.maxTargetLanguages ?? '',
            maxSessionDurationMinutes: plan.limits?.maxSessionDurationMinutes ?? ''
        },
        features: {
            ttsEnabled: plan.features?.ttsEnabled || false,
            // Nested on the stored plan, flat on the wire. An absent `broadcast` node is
            // how a catalog document written before the capability existed looks, and it
            // reads as DISABLED everywhere — so `|| false` is the correct projection, not
            // a fallback that hides a load failure.
            broadcastEnabled: plan.features?.broadcast?.enabled || false
        }
    });

    const openEdit = (plan) => {
        setSelectedPlan(plan);
        setEditForm({
            ...projectPlan(plan),
            // The admin write path requires a reason whenever any business field actually
            // changes — see businessFieldsChanged() below.
            reason: ''
        });
        setStatus({ type: '', message: '' });
    };

    /** A text input hands back strings, the stored plan holds numbers, and a cleared input is a
     *  real "not set" — so every comparable value goes through one normalizer before the diff. */
    const comparableForm = (form) => {
        const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
        const limits = form.limits || {};
        const features = form.features || {};
        return JSON.stringify({
            name: form.name || '',
            displayTier: form.displayTier || '',
            monthlyPriceCents: num(form.monthlyPriceCents),
            active: form.active !== false,
            limits: {
                maxParticipantsPerSession: num(limits.maxParticipantsPerSession),
                maxTargetLanguages: num(limits.maxTargetLanguages),
                maxSessionDurationMinutes: num(limits.maxSessionDurationMinutes)
            },
            features: {
                ttsEnabled: !!features.ttsEnabled,
                broadcastEnabled: !!features.broadcastEnabled
            }
        });
    };

    // True when ANY business field differs from what this editor loaded. The admin write path
    // widened REASON_REQUIRED from "a numeric limit moved" to every field it stores, so a
    // name-only or price-only save without a reason is now a 400.
    const businessFieldsChanged = () => {
        if (!selectedPlan) return false;
        return comparableForm(editForm) !== comparableForm(projectPlan(selectedPlan));
    };

    const handleSave = async () => {
        if (readOnly) return;
        if (!selectedPlan) return;
        setIsSaving(true);
        setStatus({ type: '', message: '' });
        const changedBusinessFields = businessFieldsChanged();
        try {
            const payload = {
                name: editForm.name || null,
                displayTier: editForm.displayTier || null,
                monthlyPriceCents: editForm.monthlyPriceCents ? parseInt(editForm.monthlyPriceCents, 10) : null,
                active: editForm.active,
                limits: {
                    maxParticipantsPerSession: editForm.limits.maxParticipantsPerSession !== '' ? parseInt(editForm.limits.maxParticipantsPerSession, 10) : null,
                    maxTargetLanguages: editForm.limits.maxTargetLanguages !== '' ? parseInt(editForm.limits.maxTargetLanguages, 10) : null,
                    maxSessionDurationMinutes: editForm.limits.maxSessionDurationMinutes !== '' ? parseInt(editForm.limits.maxSessionDurationMinutes, 10) : null
                },
                features: {
                    ttsEnabled: editForm.features.ttsEnabled,
                    broadcastEnabled: editForm.features.broadcastEnabled
                },
                // REQUIRED whenever any business field actually changes (400 REASON_REQUIRED
                // otherwise). Omitted only for a save that changes nothing at all.
                ...(changedBusinessFields ? { reason: editForm.reason } : {})
            };

            // Sprint 2 review P1: this editor sent neither If-Match nor reason, so every limit
            // edit made here was rejected once the admin write path enforced its precondition.
            // If-Match rides along whenever an etag is loaded, not only for limit edits — it is
            // harmless for any other field and is the infrastructure the server's concurrency
            // check needs regardless of which fields changed.
            const headers = { 'Content-Type': 'application/json' };
            if (selectedPlan.etag) headers['If-Match'] = selectedPlan.etag;

            const response = await fetchWithAuth(
                `${BASE_URL()}/platform/plans/${encodeURIComponent(selectedPlan.id)}`,
                {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify(payload)
                }
            );

            if (response.ok) {
                const updated = await response.json();
                setPlans(prev => prev.map(p => p.id === updated.id ? updated : p));
                setSelectedPlan(updated);
                setEditForm(f => ({ ...f, reason: '' }));
                setStatus({ type: 'success', message: `Plan "${updated.name || updated.id}" saved successfully.` });
            } else {
                // {code, message} on every error status (400/404/412/428) — show the server's
                // message verbatim rather than a raw response body.
                const body = await response.json().catch(() => ({}));
                const message = body.message || `Save failed (HTTP ${response.status}).`;
                if (response.status === 412 || response.status === 428) {
                    // A stale precondition guarantees a repeat failure — reload before retrying.
                    // fetchPlans() clears `status` at its own start, so the conflict message is
                    // set AFTER it resolves, or the reload would wipe the message it needs to show.
                    await fetchPlans();
                }
                setStatus({ type: 'error', message });
            }
        } catch (err) {
            trackException(err, { operation: 'PlanEditor.handleSave', planId: selectedPlan?.id });
            setStatus({ type: 'error', message: err.message });
        } finally {
            setIsSaving(false);
        }
    };

    // `--error`/`--success`/`--warning` are the same static hex in both themes (see
    // index-revised.css), so none can serve as TEXT on a background tinted from --bg-card: a
    // 12%-into-white (light mode) tint and a 12%-into-#1e293b (dark mode) tint sit at opposite
    // ends of the luminance range. --text-primary already flips per theme to contrast against
    // --bg-card, and a 12% tint barely moves off it, so it stays readable in both. Same pattern
    // already established in RealtimeProviderPolicySettings.js's noticeStyle/PimTiersSection.js.
    const noticeStyle = (isError) => ({
        padding: '8px 12px',
        marginBottom: 12,
        borderRadius: 4,
        fontSize: 13,
        background: isError
            ? 'color-mix(in srgb, var(--error, #dc2626) 12%, var(--bg-card, #fff))'
            : 'color-mix(in srgb, var(--success, #059669) 12%, var(--bg-card, #fff))',
        color: 'var(--text-primary, #111827)',
    });
    const warningNoticeStyle = {
        margin: '0 0 6px 24px',
        padding: '6px 10px',
        borderRadius: 4,
        fontSize: 12,
        background: 'color-mix(in srgb, var(--warning, #f59e0b) 12%, var(--bg-card, #fff))',
        color: 'var(--text-primary, #111827)',
    };

    const tableStyle = { borderCollapse: 'collapse', width: '100%' };
    const thStyle = { border: '1px solid #ddd', padding: '6px 10px', background: 'var(--bg-secondary, #f5f5f5)', color: 'var(--text-primary, #111827)', textAlign: 'left' };
    const tdStyle = { border: '1px solid #ddd', padding: '4px 8px' };
    const inputStyle = { width: '100%', padding: '4px 8px', border: '1px solid #ccc', borderRadius: 3 };
    const labelStyle = { display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 12 };

    return (
        <div style={{ padding: 24, maxWidth: 1100, display: 'flex', gap: 24 }}>
            {/* Plan List */}
            <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h2 style={{ margin: 0 }}>Plan Editor</h2>
                    <button onClick={fetchPlans} disabled={isLoading}>{isLoading ? 'Loading...' : 'Refresh'}</button>
                </div>
                <ReadOnlyNote readOnly={readOnly} />

                {status.type === 'error' && !selectedPlan && (
                    <div style={noticeStyle(true)}>
                        {status.message}
                    </div>
                )}

                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Plan ID</th>
                            <th style={thStyle}>Name</th>
                            <th style={thStyle}>Tier</th>
                            <th style={thStyle}>Price/mo</th>
                            <th style={thStyle}>Active</th>
                            <th style={thStyle}>{readOnly ? 'View' : 'Edit'}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {plans.map(plan => (
                            <tr key={plan.id} style={{ background: selectedPlan?.id === plan.id ? 'color-mix(in srgb, var(--primary) 12%, var(--bg-card, #fff))' : 'transparent' }}>
                                <td style={tdStyle}><code style={{ fontSize: 11 }}>{plan.id}</code></td>
                                <td style={tdStyle}>{plan.name || '-'}</td>
                                <td style={tdStyle}>{plan.displayTier || '-'}</td>
                                <td style={tdStyle}>{plan.monthlyPriceCents ? `$${(plan.monthlyPriceCents / 100).toFixed(2)}` : 'Free'}</td>
                                <td style={tdStyle}>{plan.active !== false ? 'Yes' : 'No'}</td>
                                <td style={tdStyle}>
                                    <button onClick={() => openEdit(plan)} style={{ fontSize: 12, padding: '2px 8px' }}>{readOnly ? 'View' : 'Edit'}</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Edit Form */}
            {selectedPlan && (
                <div style={{ width: 320, borderLeft: '1px solid #ddd', paddingLeft: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <h3 style={{ margin: 0 }}>{readOnly ? 'Plan' : 'Edit Plan'}</h3>
                        <button onClick={() => setSelectedPlan(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}>×</button>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary, #666)', marginBottom: 16 }}>{selectedPlan.id}</div>

                    {status.message && (
                        <div style={noticeStyle(status.type === 'error')}>
                            {status.message}
                        </div>
                    )}

                    <ReadOnlyFieldset readOnly={readOnly}>
                    <label style={labelStyle}>
                        Name
                        <input style={inputStyle} value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                    </label>

                    <label style={labelStyle}>
                        Display Tier
                        <input style={inputStyle} value={editForm.displayTier} onChange={e => setEditForm(f => ({ ...f, displayTier: e.target.value }))} />
                    </label>

                    <label style={labelStyle}>
                        Monthly Price (cents)
                        <input type="number" style={inputStyle} value={editForm.monthlyPriceCents} onChange={e => setEditForm(f => ({ ...f, monthlyPriceCents: e.target.value }))} />
                        <span style={{ fontSize: 11, color: 'var(--text-secondary, #666)' }}>${((editForm.monthlyPriceCents || 0) / 100).toFixed(2)}/mo</span>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <input type="checkbox" checked={editForm.active} onChange={e => setEditForm(f => ({ ...f, active: e.target.checked }))} />
                        Active
                    </label>

                    <div style={{ marginBottom: 12 }}>
                        <strong style={{ fontSize: 13 }}>Limits</strong>
                        <label style={{ ...labelStyle, marginTop: 8 }}>
                            Max Participants/Session
                            {/* type="text"/inputMode="numeric", not type="number": a real stored 0
                                must render (and be assertable) as the value "0" — jest-dom's
                                toHaveValue() coerces type="number" inputs to a JS Number, which a
                                real 0 would still satisfy, but which also silently accepts a blank
                                input as null. Parsing happens at save time (handleSave). */}
                            <input type="text" inputMode="numeric" style={inputStyle} value={editForm.limits?.maxParticipantsPerSession} onChange={e => setEditForm(f => ({ ...f, limits: { ...f.limits, maxParticipantsPerSession: e.target.value } }))} />
                        </label>
                        <label style={labelStyle}>
                            Max Target Languages
                            <input type="number" style={inputStyle} value={editForm.limits?.maxTargetLanguages} onChange={e => setEditForm(f => ({ ...f, limits: { ...f.limits, maxTargetLanguages: e.target.value } }))} />
                        </label>
                        <label style={labelStyle}>
                            Max Session Duration (min)
                            <input type="number" style={inputStyle} value={editForm.limits?.maxSessionDurationMinutes} onChange={e => setEditForm(f => ({ ...f, limits: { ...f.limits, maxSessionDurationMinutes: e.target.value } }))} />
                        </label>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <strong style={{ fontSize: 13 }}>Features</strong>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, marginBottom: 6 }}>
                            <input type="checkbox" checked={editForm.features?.ttsEnabled} onChange={e => setEditForm(f => ({ ...f, features: { ...f.features, ttsEnabled: e.target.checked } }))} />
                            TTS Enabled
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <input type="checkbox" checked={editForm.features?.broadcastEnabled} onChange={e => setEditForm(f => ({ ...f, features: { ...f.features, broadcastEnabled: e.target.checked } }))} />
                            Live Broadcast Enabled
                        </label>
                        {editForm.features?.broadcastEnabled && Number(editForm.limits?.maxParticipantsPerSession) <= 0 && (
                            <p style={warningNoticeStyle}>
                                Max Participants/Session is the listener cap. 0 disables the
                                broadcast entirely; use -1 for unlimited, or a positive number for
                                a capped audience.
                            </p>
                        )}
                    </div>

                    {businessFieldsChanged() && (
                        <label style={labelStyle}>
                            Reason for the change
                            <textarea
                                style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                                value={editForm.reason || ''}
                                onChange={e => setEditForm(f => ({ ...f, reason: e.target.value }))}
                            />
                            <span style={{ fontSize: 11, color: 'var(--text-secondary, #666)' }}>
                                Required — a stored value differs from what was loaded.
                            </span>
                        </label>
                    )}

                    </ReadOnlyFieldset>

                    {!readOnly && (
                    <button
                        onClick={handleSave}
                        disabled={isSaving || (businessFieldsChanged() && !(editForm.reason || '').trim())}
                        style={{ width: '100%', background: '#2563eb', color: 'white', border: 'none', padding: '8px 0', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                    >
                        {isSaving ? 'Saving...' : 'Save Plan'}
                    </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default PlanEditor;
